import React from 'react';
import { Save, Edit3 } from 'lucide-react';

import EditableInput from './common/EditableInput';
import AffiliateCard from './common/AffiliateCard';
import { ARC_PRODUCTS } from '../config/affiliateLinks';

/**
 * GoalsTab – smart financial goals with auto-calculations.
 *
 * Props
 *  ─ goals:        Goal[]
 *  ─ editMode:     boolean
 *  ─ onToggleEdit: () => void
 *  ─ updateGoal(index, field, value)
 *  ─ investments:  Investment[] - for calculating emergency fund current
 *  ─ totalInvestments: number - for FIRE calculations
 *  ─ totalDebts:   number - for FIRE calculations
 *  ─ expenseData:  ExpenseRow[] - for calculating expense averages
 */
const GoalsTab = ({
  goals,
  editMode,
  onToggleEdit,
  updateGoal,
  investments = [],
  totalInvestments = 0,
  totalDebts = 0,
  expenseData = [],
  wallets = []
}) => {
  // On-chain crypto holdings — uses manual override if set, else last fetched value
  const totalCryptoHoldings = wallets.reduce(
    (s, w) =>
      s +
      (typeof w.manualValue === "number" && !isNaN(w.manualValue)
        ? w.manualValue
        : w.lastValue || 0),
    0
  );
  // Helper functions
  const calculateExpenseAverage = (months = 6) => {
    if (!expenseData.length) return 0;
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-11
    
    // Filter to only include months that have occurred (current month and earlier)
    const pastAndCurrentMonths = expenseData.filter(row => {
      // Parse the month string (assuming format like "Jan 2024", "February 2024", etc.)
      const monthParts = row.month.split(' ');
      let monthName, year;
      
      if (monthParts.length === 2) {
        [monthName, year] = monthParts;
      } else {
        // If no year specified, assume current year
        monthName = monthParts[0];
        year = currentYear;
      }
      
      const yearNum = parseInt(year);
      const monthIndex = new Date(`${monthName} 1, ${yearNum}`).getMonth();
      
      // Include if it's a past year, or same year but past/current month
      return yearNum < currentYear || (yearNum === currentYear && monthIndex <= currentMonth);
    });
    
    // Get the last N months from the filtered data
    const recentExpenses = pastAndCurrentMonths.slice(-months);
    const totalExpenses = recentExpenses.reduce((sum, row) => sum + (row.total || 0), 0);
    return recentExpenses.length > 0 ? totalExpenses / recentExpenses.length : 0;
  };

  const getEmergencyFundCurrent = () => {
    const emergencyInvestments = investments.filter(inv => inv.type === 'Emergency Fund');
    return emergencyInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  };

  const getFireCurrent = () => {
    // FIRE progress = manual investments + on-chain wallets − debts
    return totalInvestments + totalCryptoHoldings - totalDebts;
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString()}`;
  };

  // Enhanced goals with auto-calculations
  const enhancedGoals = goals.map((goal, index) => {
    let enhancedGoal = { ...goal };

    switch (goal.name) {
      case 'FIRE Target':
        enhancedGoal.current = getFireCurrent();
        enhancedGoal.progress = enhancedGoal.target ? (enhancedGoal.current / enhancedGoal.target) * 100 : 0;
        break;
        
      case 'Emergency Fund':
        const avgExpenses6months = calculateExpenseAverage(6);
        const avgExpenses3months = calculateExpenseAverage(3);
        
        enhancedGoal.current = getEmergencyFundCurrent();
        enhancedGoal.target = avgExpenses6months * 6; // 6 months of expenses
        enhancedGoal.progress = enhancedGoal.target ? (enhancedGoal.current / enhancedGoal.target) * 100 : 0;
        enhancedGoal.avgExpenses3months = avgExpenses3months;
        enhancedGoal.avgExpenses6months = avgExpenses6months;
        break;
        
      default:
        // For other goals, keep original values but recalculate progress
        enhancedGoal.progress = enhancedGoal.target ? (enhancedGoal.current / enhancedGoal.target) * 100 : 0;
        break;
    }

    return enhancedGoal;
  });

  // Filter out Investment Target and add Emergency Fund + Total FIRE Amount if not exists
  let filteredGoals = enhancedGoals.filter(goal => goal.name !== 'Investment Target');
  
  // Add Emergency Fund goal if it doesn't exist
  const hasEmergencyFund = filteredGoals.some(goal => goal.name === 'Emergency Fund');
  if (!hasEmergencyFund) {
    const avgExpenses6months = calculateExpenseAverage(6);
    const avgExpenses3months = calculateExpenseAverage(3);
    
    filteredGoals.push({
      name: 'Emergency Fund',
      target: avgExpenses6months * 6,
      current: getEmergencyFundCurrent(),
      progress: (avgExpenses6months * 6) ? (getEmergencyFundCurrent() / (avgExpenses6months * 6)) * 100 : 0,
      avgExpenses3months: avgExpenses3months,
      avgExpenses6months: avgExpenses6months,
      isCalculated: true
    });
  }
  // Add Total FIRE Amount goal if it doesn't exist
  const hasFireTotal = filteredGoals.some(goal => goal.name === 'Total Amount Target needed for Fire');
  if (!hasFireTotal) {
    const emergencyGoal = filteredGoals.find(goal => goal.name === 'Emergency Fund');
    const fireGoal = filteredGoals.find(goal => goal.name === 'FIRE Target');
    
    const emergencyTarget = emergencyGoal?.target || 0;
    const fireTarget = fireGoal?.target || 0;
    const emergencyCurrent = emergencyGoal?.current || 0;
    
    filteredGoals.push({
      name: 'Total Amount Target needed for Fire',
      target: emergencyTarget + fireTarget,
      current: emergencyCurrent + getFireCurrent(),
      progress: (emergencyTarget + fireTarget) ? ((emergencyCurrent + getFireCurrent()) / (emergencyTarget + fireTarget)) * 100 : 0,
      isCalculated: true
    });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Financial Goals</h3>
        <button
          onClick={onToggleEdit}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            editMode ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
          } hover:opacity-90`}
        >
          {editMode ? <Save size={16} /> : <Edit3 size={16} />} {editMode ? 'Save' : 'Edit'}
        </button>
      </div>

      {/* Goal Cards */}
      {filteredGoals.map((goal, idx) => (
        <div key={goal.name} className="bg-arc-elevated p-6 rounded-lg shadow-md">
          {/* Title + % */}
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold">
              {editMode && !goal.isCalculated ? (
                <EditableInput
                  type="text"
                  value={goal.name}
                  onChange={(v) => updateGoal(idx, 'name', v)}
                  className="font-semibold text-lg border-none bg-transparent p-0"
                />
              ) : (
                goal.name
              )}
            </h4>
            <span className={`text-2xl font-bold ${
              goal.progress >= 100 ? 'text-green-600' : 'text-blue-600'
            }`}>
              {goal.progress.toFixed(1)}%
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-white/[0.08] rounded-full h-4 mb-4">
            <div
              className={`h-4 rounded-full transition-all duration-300 ${
                goal.progress >= 100 ? 'bg-green-600' : 'bg-blue-600'
              }`}
              style={{ width: `${Math.min(goal.progress, 100)}%` }}
            />
          </div>

          {/* Special info for Emergency Fund */}
          {goal.name === 'Emergency Fund' && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-arc-muted">3-Month Average Expenses</p>
                  <p className="font-semibold text-blue-600">
                    {formatCurrency(goal.avgExpenses3months)}
                  </p>
                </div>
                <div>
                  <p className="text-arc-muted">6-Month Average Expenses</p>
                  <p className="font-semibold text-blue-600">
                    {formatCurrency(goal.avgExpenses6months)}
                  </p>
                </div>
              </div>
              <p className="text-xs text-arc-dim mt-2">
                Target: 6 months of expenses • Current: Sum of Emergency Fund investments
              </p>
              {getEmergencyFundCurrent() === 0 && (
                <div className="mt-2 p-2 bg-orange-100 border border-orange-300 rounded">
                  <p className="text-orange-800 text-xs">
                    💡 No investments categorized as "Emergency Fund". Go to Investments tab and set some investments to "Emergency Fund" category.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Special info for FIRE Target */}
          {goal.name === 'FIRE Target' && (
            <div className="mb-4 p-3 rounded-2xl border border-arc-line bg-white/[0.02]">
              <p className="text-xs text-arc-muted mb-3">
                Formula: Manual Investments + On-Chain Wallets − Total Debts
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-arc-muted text-xs">Manual Investments</p>
                  <p className="font-semibold num text-arc-ink">{formatCurrency(totalInvestments)}</p>
                </div>
                <div>
                  <p className="text-arc-muted text-xs">On-Chain Wallets</p>
                  <p className="font-semibold num text-arc-gold">{formatCurrency(totalCryptoHoldings)}</p>
                </div>
                <div>
                  <p className="text-arc-muted text-xs">Total Debts</p>
                  <p className="font-semibold num text-red-400">−{formatCurrency(totalDebts)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Special info for Total FIRE Amount */}
          {goal.name === 'Total Amount Target needed for Fire' && (
            <div className="mb-4 p-3 bg-purple-50 rounded-lg">
              <p className="text-sm text-arc-muted">
                Debt - (Combined Emergency Fund + FIRE Target goals)
              </p>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-4 text-center">
            {/* Current */}
            <div>
              <p className="text-arc-muted text-sm">Current</p>
              <p className={`text-lg font-semibold ${goal.current < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {editMode && !goal.isCalculated && goal.name !== 'FIRE Target' && goal.name !== 'Emergency Fund' ? (
                  <EditableInput
                    value={goal.current}
                    onChange={(v) => updateGoal(idx, 'current', v)}
                    className="w-24 text-center"
                  />
                ) : (
                  formatCurrency(goal.current)
                )}
              </p>
              {(goal.name === 'FIRE Target' || goal.name === 'Emergency Fund') && (
                <p className="text-xs text-arc-dim mt-1">Auto-calculated</p>
              )}
            </div>

            {/* Target */}
            <div>
              <p className="text-arc-muted text-sm">Target</p>
              <p className="text-lg font-semibold text-blue-600">
                {editMode && !goal.isCalculated && goal.name !== 'Emergency Fund' ? (
                  <EditableInput
                    value={goal.target}
                    onChange={(v) => updateGoal(idx, 'target', v)}
                    className="w-24 text-center"
                  />
                ) : (
                  formatCurrency(goal.target)
                )}
              </p>
              {goal.name === 'Emergency Fund' && (
                <p className="text-xs text-arc-dim mt-1">6 months expenses</p>
              )}
            </div>

            {/* Remaining */}
            <div>
              <p className="text-arc-muted text-sm">Remaining</p>
              <p className={`text-lg font-semibold ${
                (goal.target - goal.current) <= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(Math.max(0, goal.target - goal.current))}
              </p>
              {(goal.target - goal.current) <= 0 && (
                <p className="text-xs text-green-600 mt-1">Goal achieved! 🎉</p>
              )}
            </div>
          </div>

          {/* Achievement badge */}
          {goal.progress >= 100 && (
            <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg">
              <p className="text-green-800 text-sm font-medium text-center">
                🎉 Congratulations! You've achieved this goal!
              </p>
            </div>
          )}
        </div>
      ))}

      {/* Summary Stats */}
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
        <h4 className="text-lg font-semibold mb-4">Goals Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-arc-muted text-sm">Goals Achieved</p>
            <p className="text-2xl font-bold text-green-600">
              {filteredGoals.filter(g => g.progress >= 100).length} / {filteredGoals.length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-arc-muted text-sm">Average Progress</p>
            <p className="text-2xl font-bold text-blue-600">
              {(filteredGoals.reduce((sum, g) => sum + g.progress, 0) / filteredGoals.length).toFixed(1)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-arc-muted text-sm">Total Target Amount</p>
            <p className="text-2xl font-bold text-purple-600">
              {formatCurrency(filteredGoals
                .filter(g => g.name !== 'Total Amount Target needed for Fire')
                .reduce((sum, g) => sum + g.target, 0)
              )}
            </p>
          </div>
        </div>
      </div>

      {/* ARC Galaxy CTA — Goals tab is the perfect surface for the $147
          entry product. User has just engaged with their FIRE journey and
          is asking "how do I actually get there?" */}
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md mt-6">
        <p className="eyebrow mb-1">Need help getting there?</p>
        <h3 className="font-semibold text-lg text-arc-ink mb-3">
          Your goals + ARC's playbook
        </h3>
        <p className="text-sm text-arc-muted mb-4">
          Setting the goal is step one. Step two is the strategy. Galaxy is
          ARC's flagship community + education program — live sessions, vault
          breakdowns, and the full Galaxy library to help you actually hit
          the targets you just set.
        </p>
        <AffiliateCard
          link={ARC_PRODUCTS.galaxy}
          medium="goals-tab-galaxy"
          featured
        />
      </div>
    </div>
  );
};

export default GoalsTab;